/*!
 * Silverback AI Search Widget v1
 * Drop-in replacement for Shopify native search modal
 * Add to theme.liquid before </body>:
 *   <script src="https://email.silverbackgymwear.com/talent/public/silverback-search-v1.js" defer></script>
 */
(function () {
  'use strict';

  // ── Language detection ────────────────────────────────────────────────────
  const locale = window.SBSearchLocale || {};
  const LANG = (locale.language || window.Shopify?.locale || document.documentElement.lang || 'en')
    .split('-')[0]
    .toLowerCase();
  const LOCALE_ROOT_URL = locale.rootUrl || (LANG === 'en' ? '/' : `/${LANG}`);
  const ACTIVE_CURRENCY = locale.currency || window.Shopify?.currency?.active || 'GBP';
  const localizedProductCache = new Map();

  const API           = 'https://email.silverbackgymwear.com/api/product-search';
  const ANALYTICS_API = 'https://email.silverbackgymwear.com/api/search-analytics';
  const strings = {
    popular: ['T-Shirts', 'Hoodies', 'Joggers', 'Shorts', 'Vests', 'Zip Hoodies', 'Bags', 'Caps', 'Lifting Gear'],
    bestsellers: '🔥 Best Sellers',
    arrivals: '✨ New Arrivals',
    recent: 'Recent searches',
    featured: 'Featured products',
    noResults: 'No results for',
    tryAnother: 'Try a different search or browse by category below',
    aiHelp: "Can't find what you're looking for? Our AI can help.",
    askAi: 'Ask our AI →',
    aiSearch: 'AI Search',
    result: 'result',
    results: 'results',
    related: 'People also searched for',
    searching: 'Searching…',
    voiceUnavailable: 'Voice not supported in this browser',
    voiceLanguage: navigator.language || LANG,
    placeholders: ['Search products, styles, colours…', 'Try "black hoodie"…', 'Try "olive joggers"…'],
    ...locale.copy
  };
  const POPULAR = strings.popular;
  // Locale files control the labels; search/category APIs keep using these stable
  // English category keys so new TLabs translations cannot break quick links.
  const POPULAR_SEARCH_TERMS = ['T-Shirts', 'Hoodies', 'Joggers', 'Shorts', 'Vests', 'Zip Hoodies', 'Bags', 'Caps', 'Lifting Gear'];
  const BESTSELLERS_PILL = strings.bestsellers;
  const NEW_ARRIVALS_PILL = strings.arrivals;
  const PRICE_MAX = 150;
  const SIZES = ['S','M','L','XL','2XL','3XL','4XL','5XL'];
  const CATEGORIES = ['T-Shirt','Hoodie','Jogger','Short','Vest','Zip Hoodie','Bag','Cap','Lifting Gear','Sock','Strap','Sleeve','Belt','Jacket'];
  const COLORS = ['black','white','red','blue','green','grey','gray','navy','yellow','purple','orange','pink','brown','tan','beige','khaki','gold','silver','dark','light','acid wash','camo'];
  const RELATED_TERMS = {
    hoodie:  ['Joggers','Zip Hoodies','T-Shirts','Vests'],
    hoodies: ['Joggers','Zip Hoodies','T-Shirts'],
    jogger:  ['Hoodie','Shorts','Vest','T-Shirts'],
    joggers: ['Hoodies','Shorts','Vests'],
    shorts:  ['Vests','T-Shirts','Joggers'],
    vest:    ['Shorts','T-Shirts','Lifting Gear'],
    vests:   ['Shorts','T-Shirts','Joggers'],
    belt:    ['Wraps','Sleeves','Lifting Gear'],
    belts:   ['Wraps','Sleeves','Lifting Gear'],
    bag:     ['Caps','Lifting Gear'],
    bags:    ['Caps','Lifting Gear'],
    cap:     ['Bags','T-Shirts'],
    caps:    ['Bags','Vests'],
    jacket:  ['Hoodies','Joggers'],
    black:   ['Navy','Charcoal','Grey'],
    olive:   ['Khaki','Stone','Sand'],
    navy:    ['Black','Stone','Grey'],
  };

  /* ── Session ID (persists for the browser session) ───────────────────────── */
  const _sbsid = (() => {
    try {
      let id = sessionStorage.getItem('_sbsid');
      if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('_sbsid', id); }
      return id;
    } catch(_) { return Math.random().toString(36).slice(2); }
  })();

  /* ── Analytics beacon ────────────────────────────────────────────────────── */
  function extractMetadata(query) {
    const lower = (query || '').toLowerCase();
    const meta = {};
    
    // Extract category
    for (const cat of CATEGORIES) {
      if (lower.includes(cat.toLowerCase())) {
        meta.category = cat;
        break;
      }
    }
    
    // Extract color
    for (const col of COLORS) {
      if (lower.includes(col)) {
        meta.color = col;
        break;
      }
    }
    
    return meta;
  }

  function trackEvent(data) {
    const metadata = extractMetadata(data.query || '');
    const payload = JSON.stringify({ 
      ...data, 
      ...metadata,  // Include extracted category/color
      session_id: _sbsid, 
      ts: Date.now() 
    });
    // Use fetch with keepalive (survives page unload, proper JSON headers)
    fetch(ANALYTICS_API, { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: payload, 
      keepalive: true 
    }).catch(() => {});
  }

  /* ── Styles ──────────────────────────────────────────────────────────────── */
  const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

  #sb-search-overlay {
    display: none; position: fixed; inset: 0; z-index: 99999;
    background: rgba(0,0,0,0.55);
    align-items: flex-start; justify-content: center;
    padding-top: 80px;
    font-family: 'Poppins', sans-serif;
  }
  #sb-search-overlay.sb-open { display: flex; animation: sb-overlay-in 0.2s ease; }
  @keyframes sb-overlay-in { from { opacity: 0; } to { opacity: 1; } }

  #sb-search-modal {
    background: #fff; color: #1a1a1a; width: 100%; max-width: 960px;
    max-height: 72vh; overflow: hidden; border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0,0,0,0.25), 0 0 0 1px #f0f0f2;
    display: flex; flex-direction: column;
    animation: sb-modal-in 0.22s cubic-bezier(0.22,1,0.36,1);
  }
  @keyframes sb-modal-in { from { transform: translateY(-12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  #sb-search-modal *, #sb-search-modal *::before, #sb-search-modal *::after {
    font-family: 'Poppins', sans-serif !important;
  }

  #sb-search-header {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 20px; flex-shrink: 0;
    background: #fff; z-index: 2;
    border-bottom: 1px solid rgba(0,0,0,0.07);
  }
  #sb-search-input-wrap {
    flex: 1; display: flex; align-items: center; gap: 12px;
    background: #f5f5f7;
    border: 1.5px solid rgba(255,255,255,0.18);
    border-radius: 99px; padding: 7px 18px;
    transition: border-color 0.18s;
  }
  #sb-search-input-wrap:focus-within {
    border-color: #666;
    background: #f0f0f2;
  }
  #sb-search-icon { color: #999; flex-shrink: 0; }
  #sb-search-input {
    flex: 1; background: none; border: none; outline: none;
    color: #1a1a1a; font-family: 'Poppins', sans-serif;
    font-size: 0.95rem; font-weight: 500; letter-spacing: 0.01em;
  }
  #sb-search-input::placeholder { color: #999; }
  #sb-search-close {
    background: #e8e8ea; border: 1.5px solid #ccc;
    color: #333; cursor: pointer;
    padding: 0; width: 36px; height: 36px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s; flex-shrink: 0;
  }
  #sb-search-close:hover { color: #000; background: #ddd; border-color: #999; }

  #sb-back-btn {
    display: none; align-items: center; gap: 7px;
    background: #f0f0f2; border: 1.5px solid rgba(0,0,0,0.15);
    color: #333; cursor: pointer;
    padding: 6px 14px; border-radius: 99px;
    font-family: 'Poppins', sans-serif; font-size: 0.75rem; font-weight: 600;
    letter-spacing: 0.04em; transition: all 0.15s; flex-shrink: 0; white-space: nowrap;
  }
  #sb-back-btn:hover { background: #e8e8ea; color: #1a1a1a; border-color: #666; }
  #sb-back-btn.sb-visible { display: flex; }

  #sb-search-body { padding: 28px 32px 36px; flex: 1; overflow-y: auto; scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.15) transparent; }
  #sb-search-body::-webkit-scrollbar { width: 4px; }
  #sb-search-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }

  .sb-section-label {
    font-family: 'Poppins', sans-serif;
    font-size: 0.72rem; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase;
    color: #aaa; margin: 0 0 16px;
  }
  .sb-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 0; }
  .sb-pill.sb-active { background: #1a1a1a !important; border-color: #1a1a1a !important; color: #fff !important; }
  .sb-pill {
    background: #fff; border: 1.5px solid rgba(0,0,0,0.2);
    color: #1a1a1a; padding: 9px 22px; border-radius: 8px; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.75rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.1em;
    transition: all 0.18s; white-space: nowrap;
  }
  .sb-pill:hover {
    background: #1a1a1a; color: #fff; transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }

  .sb-products-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  }
  .sb-product-card {
    background: #fff; border: 1px solid rgba(0,0,0,0.07); border-radius: 12px;
    overflow: hidden; text-decoration: none; color: inherit; display: flex;
    flex-direction: column; transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
    cursor: pointer; position: relative;
  }
  .sb-product-card:hover {
    border-color: rgba(0,0,0,0.15); transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
  }
  .sb-product-img-wrap {
    aspect-ratio: 3/4; overflow: hidden; background: #1c1c26; position: relative;
  }
  .sb-product-img-wrap img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    transition: transform 0.4s cubic-bezier(0.22,1,0.36,1);
  }
  .sb-product-card:hover .sb-product-img-wrap img { transform: scale(1.06); }
  .sb-product-hover-cta {
    position: absolute; bottom: 0; left: 0; right: 0;
    padding: 12px; background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%);
    opacity: 0; transform: translateY(6px); transition: all 0.2s;
    display: flex; align-items: center; justify-content: center;
  }
  .sb-product-card:hover .sb-product-hover-cta { opacity: 1; transform: translateY(0); }
  .sb-product-hover-cta span {
    font-family: 'Poppins', sans-serif; font-weight: 700;
    font-size: 0.7rem; letter-spacing: 0.1em; text-transform: uppercase;
    color: #fff; background: #1a1a1a; border: 1.5px solid #1a1a1a;
    padding: 7px 18px; border-radius: 8px;
  }
  .sb-product-info { padding: 12px 14px 14px; flex: 1; display: flex; flex-direction: column; }
  .sb-product-type {
    font-family: 'Poppins', sans-serif;
    font-size: 0.65rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: #888; margin-bottom: 4px;
  }
  .sb-product-name {
    font-size: 0.9rem; font-weight: 600; line-height: 1.35; color: #1a1a1a;
    flex: 1;
  }
  .sb-product-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; }
  .sb-product-price { font-family: 'Poppins', sans-serif; font-size: 1.05rem; font-weight: 800; color: #1a1a1a; letter-spacing: 0.02em; }
  .sb-product-colours { display: flex; gap: 4px; flex-wrap: wrap; }
  .sb-colour-dot {
    width: 9px; height: 9px; border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.2); flex-shrink: 0;
  }

  .sb-loading {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    padding: 56px 0; color: #666; font-size: 0.9rem; letter-spacing: 0.04em;
  }
  .sb-loading-dots span {
    display: inline-block; width: 8px; height: 8px; border-radius: 50%;
    background: #aaa; margin: 0 2px;
    animation: sb-bounce 1.2s 5;
  }
  .sb-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
  .sb-loading-dots span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes sb-bounce { 0%,80%,100%{transform:scale(0.55);opacity:0.3} 40%{transform:scale(1.1);opacity:1} }

  .sb-hot-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'Poppins', sans-serif;
    font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: #1a1a1a; background: rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.15); padding: 4px 12px;
    border-radius: 99px; margin-bottom: 16px;
  }
  .sb-rank-badge {
    position: absolute; top: 8px; left: 8px;
    background: #1a1a1a; color: #fff;
    font-family: 'Poppins', sans-serif; font-size: 0.62rem; font-weight: 800;
    letter-spacing: 0.06em; padding: 3px 8px; border-radius: 4px; z-index: 1;
  }
  .sb-ai-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'Poppins', sans-serif;
    font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: #666; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(0,0,0,0.15); padding: 4px 12px;
    border-radius: 99px; margin-bottom: 16px;
  }
  .sb-empty {
    text-align: center; padding: 56px 0; color: rgba(255,255,255,0.28); font-size: 0.95rem;
  }
  .sb-empty strong { color: rgba(255,255,255,0.6); }
  .sb-results-count {
    font-family: 'Poppins', sans-serif;
    font-size: 0.7rem; font-weight: 700; color: rgba(255,255,255,0.22);
    letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 16px;
  }
  .sb-low-stock {
    font-family: 'Poppins', sans-serif;
    font-size: 0.65rem; font-weight: 500; color: #666; margin-left: 4px;
  }

  /* ── Filter area — accordion ── */
  #sb-filter-area {
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0,0,0,0.07);
    background: #fff;
  }
  #sb-filter-toggle {
    display: flex; align-items: center; justify-content: space-between;
    padding: 11px 32px; cursor: pointer; user-select: none;
    gap: 10px; background: none; border: none; width: 100%; color: inherit;
  }
  #sb-filter-toggle:hover { background: rgba(0,0,0,0.05); }
  .sb-filter-toggle-left {
    display: flex; align-items: center; gap: 8px;
  }
  .sb-filter-toggle-label {
    font-family: 'Poppins', sans-serif;
    font-size: 0.62rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: #666;
  }
  .sb-filter-toggle-count {
    font-family: 'Poppins', sans-serif;
    font-size: 0.6rem; font-weight: 600; color: #1a1a1a;
    background: rgba(0,0,0,0.15); border-radius: 99px;
    padding: 1px 7px; display: none;
  }
  .sb-filter-toggle-count.sb-visible { display: inline-block !important; }
  .sb-filter-chevron {
    width: 14px; height: 14px; flex-shrink: 0;
    transition: transform 0.3s ease; color: #666;
  }
  #sb-filter-area.sb-filters-open .sb-filter-chevron { transform: rotate(180deg); }
  #sb-filter-content {
    max-height: 0; overflow: hidden;
    transition: max-height 0.35s cubic-bezier(0.4,0,0.2,1);
    display: flex; flex-direction: column; gap: 14px;
    padding: 0 32px;
  }
  #sb-filter-content.sb-filter-peek {
    transition: max-height 0.4s cubic-bezier(0.4,0,0.2,1);
  }
  #sb-filter-area.sb-filters-open #sb-filter-content {
    max-height: 300px;
    padding-bottom: 16px;
  }
  .sb-filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .sb-filter-row.sb-swatches-row { gap: 8px 12px; }
  .sb-filter-row.sb-sizes-row { gap: 6px; }
  @media (max-width: 768px) {
    #sb-filter-toggle { padding: 10px 20px; }
    #sb-filter-content { padding: 0 20px; }
    #sb-filter-area.sb-filters-open #sb-filter-content { padding-bottom: 14px; }
  }

  /* ── Price slider (compact, bottom) ── */
  #sb-price-bar {
    flex-shrink: 0; padding: 10px 32px 12px;
    border-top: 1px solid rgba(0,0,0,0.07);
    background: #1a1a1a;
  }
  .sb-price-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 10px;
  }
  .sb-price-title {
    font-family: 'Poppins', sans-serif;
    font-size: 0.62rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: #aaa;
  }
  .sb-price-value {
    font-family: 'Poppins', sans-serif;
    font-size: 0.8rem; font-weight: 600; color: #1a1a1a; letter-spacing: 0.01em;
    display: flex; align-items: center; gap: 6px;
  }
  .sb-range-wrap {
    position: relative; height: 20px; display: flex; align-items: center;
  }
  .sb-range-track {
    position: absolute; left: 0; right: 0; height: 3px;
    background: #e8e8ea; border-radius: 2px; pointer-events: none;
  }
  .sb-range-fill {
    position: absolute; height: 3px; background: #1a1a1a;
    border-radius: 2px; pointer-events: none;
  }
  .sb-range-input {
    position: absolute; width: 100%;
    -webkit-appearance: none; appearance: none;
    background: transparent; outline: none;
    height: 24px; margin: 0; pointer-events: none;
  }
  .sb-range-input::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 16px; height: 16px; border-radius: 50%;
    background: #1a1a1a; border: 2px solid #1a1a1a;
    cursor: pointer; pointer-events: all;
    box-shadow: 0 2px 8px rgba(0,0,0,0.5);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .sb-range-input::-webkit-slider-thumb:hover,
  .sb-range-input:active::-webkit-slider-thumb {
    transform: scale(1.2); box-shadow: 0 3px 12px rgba(0,0,0,0.6);
  }
  .sb-range-input::-moz-range-thumb {
    width: 16px; height: 16px; border-radius: 50%;
    background: #1a1a1a; border: 2px solid #1a1a1a;
    cursor: pointer; pointer-events: all;
    box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  }
  .sb-price-reset {
    background: none; border: 1px solid rgba(255,255,255,0.2);
    color: #999; font-family: 'Poppins', sans-serif;
    font-size: 0.6rem; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
    cursor: pointer; padding: 2px 8px; border-radius: 99px;
    transition: all 0.15s; display: none;
  }
  .sb-price-reset:hover { color: #1a1a1a; border-color: #666; }
  .sb-price-reset.sb-active { display: inline-block; }
  @media (max-width: 768px) { #sb-price-bar { padding: 8px 20px 10px; } }

  .sb-swatch-btn {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    background: none; border: none; cursor: pointer; padding: 0;
    transition: transform 0.15s;
  }
  .sb-swatch-btn:hover { transform: translateY(-1px); }
  .sb-swatch-btn:hover .sb-swatch-circle { box-shadow: 0 0 0 2px #666; }
  .sb-swatch-circle {
    width: 22px; height: 22px; border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.18);
    transition: box-shadow 0.15s;
    flex-shrink: 0;
  }
  .sb-swatch-circle.sb-swatch-white { border-color: #666; }
  .sb-swatch-label {
    font-family: 'Poppins', sans-serif;
    font-size: 0.55rem; font-weight: 500; letter-spacing: 0.03em;
    color: #666; white-space: nowrap;
    text-transform: capitalize;
  }
  .sb-swatch-btn.sb-swatch-active .sb-swatch-circle { box-shadow: 0 0 0 2.5px #1a1a1a !important; }
  .sb-swatch-btn.sb-swatch-active .sb-swatch-label { color: rgba(255,255,255,0.85) !important; }

  @media (max-width: 768px) {
    .sb-products-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    #sb-search-body { padding: 16px 20px 24px; }
    #sb-search-header { padding: 10px 14px; }
    .sb-swatches { gap: 8px 10px; }
    .sb-swatch-circle { width: 22px; height: 22px; }
  }
  @media (max-width: 600px) {
    #sb-search-overlay { padding: 0; align-items: flex-start; }
    #sb-search-modal { border-radius: 0; max-height: 100dvh; min-height: 100dvh; width: 100%; max-width: 100vw; }
    #sb-search-body { padding: 20px 16px 24px; }
    #sb-search-input-wrap { padding: 5px 12px; gap: 6px; }
    #sb-search-input { font-size: 0.9rem; }
    #sb-search-close { width: 40px; height: 40px; }
    #sb-search-close svg { width: 18px; height: 18px; }
    .sb-pill { font-size: 0.7rem; padding: 5px 10px; }
    #sb-filter-area { padding: 8px 12px; }
    #sb-filter-toggle { padding: 8px 12px; }
    #sb-filter-content { padding: 0 12px; gap: 8px; }
    #sb-filter-area.sb-filters-open #sb-filter-content { padding-bottom: 12px; }
    .sb-size-chip { padding: 3px 10px; font-size: 0.65rem; }
  }

  /* ── Autocomplete ── */
  #sb-autocomplete {
    position: fixed; z-index: 100000;
    background: #fff; border: 1px solid rgba(0,0,0,0.15);
    border-top: none; border-radius: 0 0 12px 12px;
    overflow: hidden; display: none;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  }
  #sb-autocomplete.sb-open { display: block; }
  .sb-ac-item {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 20px; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.85rem;
    color: #1a1a1a; transition: background 0.1s;
    border-bottom: 1px solid rgba(0,0,0,0.08);
  }
  .sb-ac-item:last-child { border-bottom: none; }
  .sb-ac-item:hover, .sb-ac-item.sb-ac-active { background: #f5f5f7; color: #1a1a1a; }
  .sb-ac-icon { color: #666; flex-shrink: 0; }
  .sb-ac-match { color: #1a1a1a; font-weight: 600; }

  /* ── Voice search button ── */
  #sb-voice-btn {
    background: none; border: none; color: #666; cursor: pointer;
    padding: 0; width: 22px; height: 22px; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; transition: color 0.15s;
  }
  #sb-voice-btn:hover { color: #333; }
  #sb-voice-btn.sb-listening { color: #ff4444; animation: sb-pulse 1s infinite; }
  @keyframes sb-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* ── Size chips (always visible in filter area) ── */
  .sb-size-chip {
    background: #f5f5f7; border: 1.5px solid rgba(0,0,0,0.15);
    color: #1a1a1a; padding: 4px 12px; border-radius: 99px; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.68rem; font-weight: 600;
    letter-spacing: 0.04em; transition: all 0.15s;
  }
  .sb-size-chip:hover { border-color: #999 !important; background: #e8e8ea !important; }
  .sb-size-chip.sb-active { background: #1a1a1a !important; border-color: #1a1a1a !important; color: #fff !important; }

  /* ── Urgency badge ── */
  .sb-urgency {
    font-family: 'Poppins', sans-serif; font-size: 0.62rem; font-weight: 600;
    color: rgba(255,180,80,0.9); letter-spacing: 0.03em; margin-top: 3px;
  }

  /* ── Recent searches ── */
  .sb-recent-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 28px; }
  .sb-recent-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f5f5f7; border: 1px solid rgba(0,0,0,0.15);
    color: #1a1a1a; padding: 6px 12px; border-radius: 99px; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.78rem; font-weight: 500;
    transition: all 0.15s;
  }
  .sb-recent-chip:hover { background: rgba(0,0,0,0.15); color: #1a1a1a; }
  .sb-recent-x {
    background: none; border: none; color: #aaa; cursor: pointer;
    font-size: 0.75rem; padding: 0; line-height: 1; margin-left: 2px;
    transition: color 0.15s;
  }
  .sb-recent-x:hover { color: #333; }

  /* ── Related searches ── */
  .sb-related { margin-top: 24px; }
  .sb-related-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
  .sb-related-chip {
    background: #f5f5f7; border: 1px solid rgba(0,0,0,0.08);
    color: rgba(255,255,255,0.55); padding: 5px 14px; border-radius: 99px; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.75rem; font-weight: 500;
    transition: all 0.15s;
  }
  .sb-related-chip:hover { background: rgba(255,255,255,0.09); color: #1a1a1a; }

  /* ── AI chat fallback (zero results) ── */
  .sb-ai-fallback {
    margin-top: 20px; padding: 16px 20px; background: #f5f5f7;
    border: 1px solid rgba(0,0,0,0.08); border-radius: 12px;
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .sb-ai-fallback-text { font-family: 'Poppins', sans-serif; font-size: 0.82rem; color: rgba(255,255,255,0.6); }
  .sb-ai-fallback-btn {
    background: #1a1a1a; color: #fff; border: none; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-size: 0.72rem; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase; padding: 8px 16px;
    border-radius: 99px; white-space: nowrap; transition: opacity 0.15s; flex-shrink: 0;
  }
  .sb-ai-fallback-btn:hover { opacity: 0.85; }

  /* ── New arrivals badge ── */
  .sb-new-badge {
    position: absolute; top: 8px; right: 8px; z-index: 1;
    background: #1a1a1a; color: #fff;
    font-family: 'Poppins', sans-serif; font-size: 0.58rem; font-weight: 800;
    letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 7px; border-radius: 4px;
  }

  /* ── Personalised greeting ── */
  .sb-greeting { font-family: 'Poppins', sans-serif; font-size: 0.78rem; color: rgba(255,255,255,0.45); margin-bottom: 16px; }
  .sb-greeting strong { color: #333; }
  `;

  /* ── Colour name → hex ───────────────────────────────────────────────────── */
  const COLOUR_MAP = {
    black:'#1a1a1a', white:'#f5f5f5', grey:'#888', gray:'#888',
    olive:'#6b7645', khaki:'#b5a642', navy:'#1a2a4a', blue:'#2255aa',
    green:'#2d7a3a', red:'#cc2222', pink:'#e87090', brown:'#7a4e2d',
    tan:'#c8a070', cream:'#f5f0e0', 'dark grey':'#444', charcoal:'#333',
    stone:'#b0a898', sand:'#d4c498', burgundy:'#6e1c2d', purple:'#5c3a8a',
  };
  // Swatches shown in empty state — most common SB colourways
  const SWATCHES = [
    { name: 'Black',    hex: '#1a1a1a' },
    { name: 'White',    hex: '#f5f5f5' },
    { name: 'Grey',     hex: '#888' },
    { name: 'Charcoal', hex: '#3a3a3a' },
    { name: 'Navy',     hex: '#1a2a4a' },
    { name: 'Stone',    hex: '#b0a898' },
    { name: 'Sand',     hex: '#d4c498' },
    { name: 'Olive',    hex: '#6b7645' },
    { name: 'Khaki',    hex: '#b5a642' },
    { name: 'Red',      hex: '#cc2222' },
  ];
  function colourHex(name) {
    const key = name.toLowerCase().trim();
    return COLOUR_MAP[key] || '#666';
  }

  /* ── State ───────────────────────────────────────────────────────────────── */
  let priceMin = 0, priceMax = PRICE_MAX;
  let selectedSize = '';
  let selectedCategory = '';
  let selectedColour = '';
  let isBestsellers = false;
  let isNewArrivals = false;

  /* ── DOM ─────────────────────────────────────────────────────────────────── */
  let overlay, input, body, debounceTimer, lastQuery = null, _searchReq = 0, _loadingTimer = null;

  function inject() {
    // Styles
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    // Overlay
    overlay = document.createElement('div');
    overlay.id = 'sb-search-overlay';
    overlay.innerHTML = `
      <div id="sb-search-modal" role="dialog" aria-modal="true" aria-label="Search">
        <div id="sb-search-header">
          <div id="sb-search-input-wrap">
            <span id="sb-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input id="sb-search-input" type="text" placeholder="" autocomplete="off" spellcheck="false" />
            <button id="sb-voice-btn" aria-label="Voice search" title="Voice search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
            <button id="sb-search-clear" aria-label="Clear search" style="display:none;background:rgba(0,0,0,0.08);border:none;color:rgba(255,255,255,0.6);cursor:pointer;width:22px;height:22px;border-radius:50%;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.15s">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div id="sb-autocomplete" role="listbox"></div>
          <button id="sb-back-btn" aria-label="Back to search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </button>
          <button id="sb-search-close" aria-label="Close search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div id="sb-filter-area">
          <button id="sb-filter-toggle" aria-expanded="false" aria-controls="sb-filter-content">
            <span class="sb-filter-toggle-left">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" style="color:#aaa">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
              </svg>
              <span class="sb-filter-toggle-label">Filters</span>
              <span class="sb-filter-toggle-count" id="sb-filter-count"></span>
            </span>
            <svg class="sb-filter-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <div id="sb-filter-content" role="region" aria-label="Search filters">
            <div class="sb-filter-row">
              ${[BESTSELLERS_PILL, NEW_ARRIVALS_PILL, ...POPULAR].map((p, i) => {
                if (i === 0) return `<button class="sb-pill sb-pill-hot" id="sb-pill-bs">${p}</button>`;
                if (i === 1) return `<button class="sb-pill" id="sb-pill-na">${p}</button>`;
                return `<button class="sb-pill sb-pill-cat" data-q="${POPULAR_SEARCH_TERMS[i - 2] || p}">${p}</button>`;
              }).join('')}
            </div>

          </div>
        </div>
        <div id="sb-search-body"></div>
      </div>`;
    document.body.appendChild(overlay);

    input = document.getElementById('sb-search-input');
    body  = document.getElementById('sb-search-body');
    initFilterAccordion();

    // Events
    document.getElementById('sb-search-close').addEventListener('click', close);
    document.getElementById('sb-back-btn').addEventListener('click', goBack);
    document.getElementById('sb-voice-btn').addEventListener('click', startVoiceSearch);

    // Autocomplete keyboard navigation
    input.addEventListener('keydown', e => {
      const ac = document.getElementById('sb-autocomplete');
      const items = ac ? [...ac.querySelectorAll('.sb-ac-item')] : [];
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); _acActive = Math.min(_acActive + 1, items.length - 1); items.forEach((el, i) => el.classList.toggle('sb-ac-active', i === _acActive)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); _acActive = Math.max(_acActive - 1, -1); items.forEach((el, i) => el.classList.toggle('sb-ac-active', i === _acActive)); }
      else if (e.key === 'Enter' && _acActive >= 0) { e.preventDefault(); items[_acActive].click(); }
    });
    const clearBtn = document.getElementById('sb-search-clear');
    clearBtn.addEventListener('click', () => { input.value = ''; input.focus(); lastQuery = null; clearBtn.style.display = 'none'; renderPromoted(); startPlaceholderCycle(); });
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        const backBtn = document.getElementById('sb-back-btn');
        if (backBtn && backBtn.classList.contains('sb-visible')) { goBack(); }
        else if (input.value) { input.value = ''; lastQuery = null; clearBtn.style.display = 'none'; renderPromoted(); startPlaceholderCycle(); }
        else { close(); }
      }
    });
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') { clearTimeout(debounceTimer); hideAutocomplete(); doSearch(input.value.trim()); } });
    input.addEventListener('blur', () => setTimeout(hideAutocomplete, 200));

    // ── Filter area wiring ──
    // Pills
    document.getElementById('sb-pill-bs').addEventListener('click', () => {
      isBestsellers = !isBestsellers;
      if (isBestsellers) { isNewArrivals = false; selectedCategory = ''; document.getElementById('sb-pill-na').classList.remove('sb-active'); clearPillCats(); }
      document.getElementById('sb-pill-bs').classList.toggle('sb-active', isBestsellers);
      applyFilters();
    });
    document.getElementById('sb-pill-na').addEventListener('click', () => {
      isNewArrivals = !isNewArrivals;
      if (isNewArrivals) { isBestsellers = false; selectedCategory = ''; document.getElementById('sb-pill-bs').classList.remove('sb-active'); clearPillCats(); }
      document.getElementById('sb-pill-na').classList.toggle('sb-active', isNewArrivals);
      applyFilters();
    });
    document.querySelectorAll('.sb-pill-cat').forEach(btn => {
      btn.addEventListener('click', () => {
        const cat = btn.dataset.q;
        if (selectedCategory === cat) {
          selectedCategory = '';
          btn.classList.remove('sb-active');
        } else {
          selectedCategory = cat;
          isBestsellers = false; isNewArrivals = false;
          document.getElementById('sb-pill-bs').classList.remove('sb-active');
          document.getElementById('sb-pill-na').classList.remove('sb-active');
          clearPillCats(btn);
          btn.classList.add('sb-active');
        }
        applyFilters();
      });
    });
    // Size chips
    document.querySelectorAll('#sb-filter-sizes .sb-size-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const sz = chip.dataset.size;
        if (selectedSize === sz) {
          selectedSize = '';
          chip.classList.remove('sb-active');
        } else {
          selectedSize = sz;
          document.querySelectorAll('#sb-filter-sizes .sb-size-chip').forEach(c => c.classList.remove('sb-active'));
          chip.classList.add('sb-active');
        }
        applyFilters();
      });
    });
  }

  function clearPillCats(except) {
    document.querySelectorAll('.sb-pill-cat').forEach(b => { if (b !== except) b.classList.remove('sb-active'); });
  }

  /* ── Price slider ────────────────────────────────────────────────────────── */
  function updatePriceFill(lo, hi) {
    const fill = document.getElementById('sb-range-fill');
    if (!fill) return;
    fill.style.left  = (lo / PRICE_MAX * 100) + '%';
    fill.style.width = ((hi - lo) / PRICE_MAX * 100) + '%';
  }

  function setPriceDisplay(lo, hi) {
    const display = document.getElementById('sb-price-display');
    const reset   = document.getElementById('sb-price-reset');
    if (!display) return;
    const isAll = lo === 0 && hi === PRICE_MAX;
    display.textContent = isAll ? 'All prices' : lo === 0 ? `Under £${hi}` : `£${lo} — £${hi}`;
    if (reset) reset.classList.toggle('sb-active', !isAll);
  }

  function initPriceSlider() {
    const minInput = document.getElementById('sb-price-min-input');
    const maxInput = document.getElementById('sb-price-max-input');
    const resetBtn = document.getElementById('sb-price-reset');
    if (!minInput || !maxInput) return;

    function onSlide() {
      let lo = parseInt(minInput.value);
      let hi = parseInt(maxInput.value);
      const GAP = 10;
      if (lo > hi - GAP) {
        if (document.activeElement === minInput) { lo = hi - GAP; minInput.value = lo; }
        else { hi = lo + GAP; maxInput.value = hi; }
      }
      priceMin = lo; priceMax = hi;
      updatePriceFill(lo, hi);
      setPriceDisplay(lo, hi);
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { lastQuery = null; triggerSearch(); }, 280);
    }

    minInput.addEventListener('input', onSlide);
    maxInput.addEventListener('input', onSlide);

    resetBtn && resetBtn.addEventListener('click', () => {
      minInput.value = 0; maxInput.value = PRICE_MAX;
      priceMin = 0; priceMax = PRICE_MAX;
      updatePriceFill(0, PRICE_MAX);
      setPriceDisplay(0, PRICE_MAX);
      lastQuery = null; triggerSearch();
    });

    updatePriceFill(0, PRICE_MAX);
  }

  function triggerSearch() {
    const q = input ? input.value.trim() : '';
    if (q) { lastQuery = null; doSearch(q); }
    else   { applyFilters(); }
  }

  function applyFilters() {
    // Update filter count badge + auto-expand accordion when a filter is selected
    updateFilterCount();
    const hasFilter = selectedCategory || selectedSize || isBestsellers || isNewArrivals || priceMin > 0 || priceMax < PRICE_MAX;
    if (hasFilter) setFiltersOpen(true);

    // Clear text input (filters drive results when no typed query)
    const cb = document.getElementById('sb-search-clear');
    if (input) { input.value = ''; stopPlaceholderCycle(); }
    if (cb) cb.style.display = 'none';

    if (isBestsellers) { renderWeeklyBestsellers(); return; }
    if (isNewArrivals) { renderNewArrivals(); return; }

    // If category selected without text search, show category bestsellers
    if (selectedCategory && !selectedSize && priceMin === 0 && priceMax === PRICE_MAX) {
      renderCategoryBestsellers(selectedCategory);
      return;
    }

    const parts = [selectedCategory].filter(Boolean);
    const q = parts.join(' ').trim();

    if (!q && !selectedSize && priceMin === 0 && priceMax === PRICE_MAX) {
      lastQuery = null;
      startPlaceholderCycle();
      renderPromoted();
      return;
    }
    lastQuery = null;
    doSearch(q);
  }

  /* ── Back navigation ────────────────────────────────────────────────────── */
  function showBackBtn(label) {
    const btn = document.getElementById('sb-back-btn');
    if (btn) { btn.classList.add('sb-visible'); }
    // Hide the input wrap so header stays clean (back btn + close only)
    const wrap = document.getElementById('sb-search-input-wrap');
    if (wrap) wrap.style.display = 'none';
  }
  function hideBackBtn() {
    const btn = document.getElementById('sb-back-btn');
    if (btn) btn.classList.remove('sb-visible');
    const wrap = document.getElementById('sb-search-input-wrap');
    if (wrap) wrap.style.display = '';
  }
  function goBack() {
    hideBackBtn();
    hideSizeBar();
    lastQuery = null;
    input.value = '';
    const cb = document.getElementById('sb-search-clear');
    if (cb) cb.style.display = 'none';
    renderPromoted();
    startPlaceholderCycle();
  }

  /* ── Recent searches ────────────────────────────────────────────────────── */
  function loadRecentSearches() {
    try { return JSON.parse(localStorage.getItem('_sb_recent') || '[]').filter(Boolean).slice(0, 6); } catch(_) { return []; }
  }
  function saveRecentSearch(q) {
    if (!q || q.length < 2) return;
    try {
      let arr = loadRecentSearches().filter(r => r.toLowerCase() !== q.toLowerCase());
      arr.unshift(q);
      localStorage.setItem('_sb_recent', JSON.stringify(arr.slice(0, 6)));
    } catch(_) {}
  }
  function clearRecentSearch(q) {
    try {
      const arr = loadRecentSearches().filter(r => r.toLowerCase() !== q.toLowerCase());
      localStorage.setItem('_sb_recent', JSON.stringify(arr));
    } catch(_) {}
  }

  /* ── Voice search ────────────────────────────────────────────────────────── */
  function startVoiceSearch() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { input.placeholder = strings.voiceUnavailable; return; }
    const rec = new SR();
    rec.lang = strings.voiceLanguage; rec.interimResults = false; rec.maxAlternatives = 1;
    const voiceBtn = document.getElementById('sb-voice-btn');
    voiceBtn.classList.add('sb-listening');
    stopPlaceholderCycle();
    input.placeholder = '🎤 Listening…';
    rec.onresult = e => {
      const transcript = e.results[0][0].transcript;
      input.value = transcript;
      const cb = document.getElementById('sb-search-clear');
      if (cb) cb.style.display = 'flex';
      doSearch(transcript);
    };
    rec.onerror = rec.onend = () => {
      voiceBtn.classList.remove('sb-listening');
      if (!input.value) startPlaceholderCycle();
    };
    rec.start();
  }

  /* ── Autocomplete ────────────────────────────────────────────────────────── */
  let _acTimer = null, _acActive = -1;
  const SUGGESTIONS_API = 'https://email.silverbackgymwear.com/api/product-suggestions';

  async function showAutocomplete(q) {
    const el = document.getElementById('sb-autocomplete');
    if (!el || !q || q.length < 2) { hideAutocomplete(); return; }
    clearTimeout(_acTimer);
    _acTimer = setTimeout(async () => {
      try {
        const res  = await fetch(`${SUGGESTIONS_API}?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        const sugs = data.suggestions || [];
        if (!sugs.length) { hideAutocomplete(); return; }
        if (!input.value.trim()) { hideAutocomplete(); return; } // input was cleared
        _acActive = -1;
        el.innerHTML = sugs.map((s, i) => {
          const icon = s.type === 'product'
            ? `<svg class="sb-ac-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`
            : `<svg class="sb-ac-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`;
          const highlighted = escHtml(s.label).replace(new RegExp(`(${escHtml(q)})`, 'gi'), '<span class="sb-ac-match">$1</span>');
          return `<div class="sb-ac-item" data-q="${escHtml(s.query)}" data-idx="${i}" role="option">${icon}<span>${highlighted}</span></div>`;
        }).join('');
        // Position below the search input wrap
        const wrap = document.getElementById('sb-search-input-wrap');
        if (wrap) {
          const r = wrap.getBoundingClientRect();
          el.style.left   = r.left + 'px';
          el.style.top    = (r.bottom - 1) + 'px';
          el.style.width  = r.width + 'px';
        }
        el.classList.add('sb-open');
        el.querySelectorAll('.sb-ac-item').forEach(item => {
          item.addEventListener('click', () => {
            input.value = item.dataset.q;
            hideAutocomplete();
            stopPlaceholderCycle();
            const cb = document.getElementById('sb-search-clear');
            if (cb) cb.style.display = 'flex';
            doSearch(item.dataset.q);
          });
        });
      } catch(_) {}
    }, 150);
  }
  function hideAutocomplete() {
    const el = document.getElementById('sb-autocomplete');
    if (el) { el.classList.remove('sb-open'); el.innerHTML = ''; }
    _acActive = -1;
  }

  /* ── Size filter ─────────────────────────────────────────────────────────── */
  function initSizeFilter() {
    const chips = document.querySelectorAll('.sb-size-chip');
    const resetBtn = document.getElementById('sb-size-reset');
    chips.forEach(chip => {
      chip.addEventListener('click', () => {
        const s = chip.dataset.size;
        if (selectedSize === s) {
          selectedSize = '';
          chip.classList.remove('sb-active');
        } else {
          selectedSize = s;
          chips.forEach(c => c.classList.remove('sb-active'));
          chip.classList.add('sb-active');
        }
        if (resetBtn) resetBtn.classList.toggle('sb-active', !!selectedSize);
        lastQuery = null; triggerSearch();
      });
    });
    resetBtn && resetBtn.addEventListener('click', () => {
      selectedSize = '';
      chips.forEach(c => c.classList.remove('sb-active'));
      resetBtn.classList.remove('sb-active');
      lastQuery = null; triggerSearch();
    });
  }
  function showSizeBar() {}
  function hideSizeBar() {}
  function resetSizeFilter() {
    selectedSize = '';
    document.querySelectorAll('#sb-filter-sizes .sb-size-chip').forEach(c => c.classList.remove('sb-active'));
  }

  /* ── Related searches ────────────────────────────────────────────────────── */
  function getRelatedSearches(q) {
    if (!q) return [];
    const words = q.toLowerCase().split(/\s+/);
    for (const w of words) {
      if (RELATED_TERMS[w]) return RELATED_TERMS[w].slice(0, 4);
    }
    return [];
  }

  /* ── Urgency text from inventory ─────────────────────────────────────────── */
  function getUrgencyText(p) {
    const inStock = p.inStock || [];
    if (!inStock.length) return '';
    if (inStock.length <= 2) return `Low stock`;
    if (inStock.length <= 5) return `Low stock`;
    return '';
  }

  /* ── New Arrivals ────────────────────────────────────────────────────────── */
  async function renderNewArrivals() {
    lastQuery = '__new_arrivals__';
    input.value = '';
    const cb = document.getElementById('sb-search-clear');
    if (cb) cb.style.display = 'none';
    stopPlaceholderCycle();
    showBackBtn();
    renderLoading();
    try {
      let url = `${API}?new_arrivals=1&limit=1000&lang=${LANG}`;
      if (selectedSize)   url += `&size=${encodeURIComponent(selectedSize)}`;
      if (priceMin > 0)   url += `&minPrice=${priceMin}`;
      if (priceMax < PRICE_MAX) url += `&maxPrice=${priceMax}`;
      const res  = await fetch(url);
      const data = await res.json();
      const products = await localizeProducts(data.results || []);
      clearTimeout(_loadingTimer);
      const badge = `<div class="sb-ai-badge" style="border-color:rgba(0,0,0,0.15)">✨ New Arrivals — latest drops</div>`;
      const count = `<div class="sb-results-count">${products.length} product${products.length !== 1 ? 's' : ''}</div>`;
      body.innerHTML = `${badge}${count}<div class="sb-products-grid">${products.map((p, i) => newArrivalCard(p, i)).join('')}</div>`;
      body.querySelectorAll('.sb-product-card').forEach(wireCard);
    } catch(_) {
      body.innerHTML = `<div class="sb-empty">Could not load new arrivals.</div>`;
    }
  }
  function newArrivalCard(p, idx) {
    const img = p.image ? `<img src="${escHtml(p.image)}" alt="${escHtml(p.title)}" loading="lazy" />` : '';
    const inStock = (p.inStock || []).length > 0;
    const colours = (p.colours || []).slice(0, 6);
    const colourDots = colours.map(c => `<span class="sb-colour-dot" style="background:${colourHex(c)}" title="${escHtml(c)}"></span>`).join('');
    return `
      <a class="sb-product-card" href="${escHtml(localizedProductUrl(p.url))}" data-url="${escHtml(localizedProductUrl(p.url))}" data-handle="${escHtml(p.handle||'')}" data-title="${escHtml(p.title)}" data-price="${escHtml(p.price||'')}" data-pos="${idx}">
        <div class="sb-product-img-wrap">
          <div class="sb-new-badge">NEW</div>${img}
          <div class="sb-product-hover-cta"><span>VIEW PRODUCT</span></div>
        </div>
        <div class="sb-product-info">
          <div class="sb-product-type">${escHtml(p.type||'')}</div>
          <div class="sb-product-name">${escHtml(p.title)}</div>
          <div class="sb-product-footer">
            <div class="sb-product-price">${escHtml(p.price||'')}</div>
            ${colourDots ? `<div class="sb-product-colours">${colourDots}</div>` : ''}
          </div>
        </div>
      </a>`;
  }

  /* ── Animated placeholder ────────────────────────────────────────────────── */
  const PLACEHOLDERS = strings.placeholders;

  let _phIdx = 0, _phTimer = null, _phActive = false;

  function startPlaceholderCycle() {
    if (_phActive) return;
    _phActive = true;
    _phIdx = 0;
    animatePlaceholder();
  }

  function stopPlaceholderCycle() {
    _phActive = false;
    clearTimeout(_phTimer);
    if (input) input.placeholder = '';
  }

  function animatePlaceholder() {
    if (!_phActive || !input || input.value) return;
    const phrase = PLACEHOLDERS[_phIdx % PLACEHOLDERS.length];
    typePhrase(phrase, 0, () => {
      // Pause then delete
      _phTimer = setTimeout(() => deletePhrase(phrase.length, () => {
        _phIdx++;
        _phTimer = setTimeout(animatePlaceholder, 400);
      }), 1800);
    });
  }

  function typePhrase(phrase, pos, done) {
    if (!_phActive || !input || input.value) return;
    input.placeholder = phrase.slice(0, pos);
    if (pos < phrase.length) {
      _phTimer = setTimeout(() => typePhrase(phrase, pos + 1, done), 55);
    } else {
      done();
    }
  }

  function deletePhrase(len, done) {
    if (!_phActive || !input || input.value) return;
    const phrase = PLACEHOLDERS[_phIdx % PLACEHOLDERS.length];
    input.placeholder = phrase.slice(0, len);
    if (len > 0) {
      _phTimer = setTimeout(() => deletePhrase(len - 1, done), 28);
    } else {
      done();
    }
  }

  function resetPriceSlider() {
    const minInput = document.getElementById('sb-price-min-input');
    const maxInput = document.getElementById('sb-price-max-input');
    if (minInput) minInput.value = 0;
    if (maxInput) maxInput.value = PRICE_MAX;
    priceMin = 0; priceMax = PRICE_MAX;
    updatePriceFill(0, PRICE_MAX);
    setPriceDisplay(0, PRICE_MAX);
  }

  /* ── Filter accordion ────────────────────────────────────────────────────── */
  // Elements assigned after DOM is built (see initFilterAccordion)
  let filterArea, filterToggle, filterContent, filterCount;

  function updateFilterCount() {
    if (!filterCount) return;
    let n = 0;
    if (selectedCategory) n++;
    if (selectedSize)     n++;
    if (isBestsellers)    n++;
    if (isNewArrivals)    n++;
    if (priceMin > 0 || priceMax < PRICE_MAX) n++;
    filterCount.textContent = n;
    filterCount.classList.toggle('sb-visible', n > 0);
  }

  function setFiltersOpen(open) {
    if (!filterArea) return;
    filterArea.classList.toggle('sb-filters-open', open);
    filterToggle.setAttribute('aria-expanded', String(open));
  }

  function initFilterAccordion() {
    filterArea    = document.getElementById('sb-filter-area');
    filterToggle  = document.getElementById('sb-filter-toggle');
    filterContent = document.getElementById('sb-filter-content');
    filterCount   = document.getElementById('sb-filter-count');
    filterToggle.addEventListener('click', () => {
      setFiltersOpen(!filterArea.classList.contains('sb-filters-open'));
    });
  }

  function filterPeek() {
    // Briefly expand to peek height, then collapse — hints filters exist
    if (!filterContent || filterArea.classList.contains('sb-filters-open')) return;
    filterContent.style.maxHeight = '72px';
    setTimeout(() => {
      filterContent.style.maxHeight = '0';
      setTimeout(() => { filterContent.style.maxHeight = ''; }, 400);
    }, 700);
  }

  /* ── Open / Close ────────────────────────────────────────────────────────── */
  function open(prefill) {
    overlay.classList.add('sb-open');
    document.body.style.overflow = 'hidden';
    setFiltersOpen(false); // always start collapsed
    setTimeout(() => {
      input.focus();
      if (prefill) { input.value = prefill; stopPlaceholderCycle(); doSearch(prefill); }
      else { lastQuery = null; renderPromoted(); startPlaceholderCycle(); }
    }, 50);
    // Peek animation after modal settles
    setTimeout(filterPeek, 420);
  }

  function close() {
    overlay.classList.remove('sb-open');
    document.body.style.overflow = '';
    input.value = '';
    stopPlaceholderCycle();
    hideAutocomplete();
    hideBackBtn();
    // Reset filter states
    selectedCategory = ''; selectedColour = ''; selectedSize = '';
    isBestsellers = false; isNewArrivals = false;
    updateFilterCount();
    setFiltersOpen(false);
    // Clear active states in filter area
    document.querySelectorAll('#sb-filter-area .sb-pill').forEach(b => b.classList.remove('sb-active'));
    document.querySelectorAll('#sb-filter-sizes .sb-size-chip').forEach(b => b.classList.remove('sb-active'));
    const cb = document.getElementById('sb-search-clear');
    if (cb) cb.style.display = 'none';
    resetPriceSlider();
  }

  /* ── Input handler ───────────────────────────────────────────────────────── */
  function onInput() {
    const q = input.value.trim();
    const clearBtn = document.getElementById('sb-search-clear');
    if (clearBtn) clearBtn.style.display = input.value ? 'flex' : 'none';
    if (input.value) { stopPlaceholderCycle(); showAutocomplete(q); }
    if (!q) { lastQuery = null; hideAutocomplete(); renderPromoted(); startPlaceholderCycle(); return; }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => doSearch(q), 260);
  }

  /* ── Search ──────────────────────────────────────────────────────────────── */
  async function doSearch(q) {
    if (!q && !selectedSize && priceMin === 0 && priceMax === PRICE_MAX) { renderPromoted(); return; }
    const reqId = ++_searchReq;
    lastQuery = q;
    hideBackBtn();
    renderLoading();
    try {
      let url = `${API}?limit=1000&lang=${LANG}`;
      if (q) url += `&q=${encodeURIComponent(q)}`;
      if (priceMin > 0)          url += `&minPrice=${priceMin}`;
      if (priceMax < PRICE_MAX)  url += `&maxPrice=${priceMax}`;
      if (selectedSize)          url += `&size=${encodeURIComponent(selectedSize)}`;
      const res  = await fetch(url);
      const data = await res.json();
      if (reqId !== _searchReq) return; // stale response
      const results = data.results || [];
      await renderResults(results, data.mode, q, () => reqId === _searchReq);
      if (reqId !== _searchReq) return;
      if (q) saveRecentSearch(q);
      trackEvent({ type: 'search', query: q, results_count: results.length, mode: data.mode || 'keyword' });
    } catch(_) {
      if (reqId !== _searchReq) return;
      body.innerHTML = `<div class="sb-empty">Something went wrong — try again.</div>`;
    }
  }

  /* ── Weekly bestsellers ──────────────────────────────────────────────────── */
  async function renderWeeklyBestsellers() {
    lastQuery = '__bestsellers_week__';
    input.value = '';
    const cb = document.getElementById('sb-search-clear');
    if (cb) cb.style.display = 'none';
    stopPlaceholderCycle();
    showBackBtn();
    renderLoading();
    try {
      let url = `${API}?bestsellers_week=1&lang=${LANG}`;
      if (selectedSize)   url += `&size=${encodeURIComponent(selectedSize)}`;
      if (priceMin > 0)   url += `&minPrice=${priceMin}`;
      if (priceMax < PRICE_MAX) url += `&maxPrice=${priceMax}`;
      const res  = await fetch(url);
      const data = await res.json();
      const products = await localizeProducts(data.results || []);
      clearTimeout(_loadingTimer);
      const badge = `<div class="sb-hot-badge">🔥 Best Sellers This Week</div>`;
      const count = `<div class="sb-results-count">${products.length} product${products.length !== 1 ? 's' : ''}</div>`;
      body.innerHTML = `${badge}${count}<div class="sb-products-grid">${products.map((p, i) => bestsellerCard(p, i)).join('')}</div>`;
      body.querySelectorAll('.sb-product-card').forEach(wireCard);
    } catch(_) {
      body.innerHTML = `<div class="sb-empty">Could not load bestsellers — try again.</div>`;
    }
  }

  function bestsellerCard(p, idx) {
    const colours = (p.colours || []).slice(0, 6);
    const colourDots = colours.map(c =>
      `<span class="sb-colour-dot" style="background:${colourHex(c)}" title="${escHtml(c)}"></span>`
    ).join('');
    const img = p.image
      ? `<img src="${escHtml(p.image)}" alt="${escHtml(p.title)}" loading="lazy" />`
      : `<div style="width:100%;height:100%;background:#1c1c26"></div>`;
    const rank = idx < 3 ? `<div class="sb-rank-badge">#${idx + 1}</div>` : '';
    const inStock = (p.inStock || []).length > 0;
    const netStr = '';
    return `
      <a class="sb-product-card" href="${escHtml(localizedProductUrl(p.url))}"
         data-url="${escHtml(localizedProductUrl(p.url))}"
         data-handle="${escHtml(p.handle || '')}"
         data-title="${escHtml(p.title)}"
         data-price="${escHtml(p.price || '')}"
         data-pos="${idx}">
        <div class="sb-product-img-wrap">
          ${rank}${img}
          <div class="sb-product-hover-cta"><span>VIEW PRODUCT</span></div>
        </div>
        <div class="sb-product-info">
          <div class="sb-product-type">${escHtml(p.type || '')}</div>
          <div class="sb-product-name">${escHtml(p.title)}</div>
          <div class="sb-product-footer">
            <div class="sb-product-price">${escHtml(p.price || '')}${netStr}${!inStock ? '<span class="sb-low-stock">· Low stock</span>' : ''}</div>
            ${colourDots ? `<div class="sb-product-colours">${colourDots}</div>` : ''}
          </div>
        </div>
      </a>`;
  }

  /* ── Category Bestsellers (14 days) ─────────────────────────────────────── */
  async function renderCategoryBestsellers(category) {
    lastQuery = '__cat_bestsellers__' + category;
    input.value = '';
    const cb = document.getElementById('sb-search-clear');
    if (cb) cb.style.display = 'none';
    stopPlaceholderCycle();
    showBackBtn();
    renderLoading();
    try {
      let url = `${API}?category_bestsellers=${encodeURIComponent(category)}&lang=${LANG}`;
      if (selectedSize)   url += `&size=${encodeURIComponent(selectedSize)}`;
      if (priceMin > 0)   url += `&minPrice=${priceMin}`;
      if (priceMax < PRICE_MAX) url += `&maxPrice=${priceMax}`;
      const res  = await fetch(url);
      const data = await res.json();
      const products = await localizeProducts(data.results || []);
      clearTimeout(_loadingTimer);
      const badge = `<div class="sb-hot-badge">🔥 ${escHtml(category)} Best Sellers (14 days)</div>`;
      const count = `<div class="sb-results-count">${products.length} product${products.length !== 1 ? 's' : ''}</div>`;
      body.innerHTML = `${badge}${count}<div class="sb-products-grid">${products.map((p, i) => bestsellerCard(p, i)).join('')}</div>`;
      body.querySelectorAll('.sb-product-card').forEach(wireCard);
    } catch(_) {
      body.innerHTML = `<div class="sb-empty">Could not load bestsellers — try again.</div>`;
    }
  }

  /* ── Promoted (empty state) ──────────────────────────────────────────────── */
  async function renderPromoted() {
    hideBackBtn();
    clearTimeout(_loadingTimer);
    try {
      const res  = await fetch(`${API}?limit=1000&lang=${LANG}`);
      const data = await res.json();
      clearTimeout(_loadingTimer);
      const products = await localizeProducts(data.results || []);
      const recent = loadRecentSearches();
      const recentHtml = recent.length
        ? `<p class="sb-section-label">${strings.recent}</p><div class="sb-recent-chips">${recent.map(r => `<span class="sb-recent-chip"><span class="sb-recent-q" data-q="${escHtml(r)}">${escHtml(r)}</span><button class="sb-recent-x" data-clear="${escHtml(r)}">✕</button></span>`).join('')}</div>`
        : '';
      body.innerHTML = `
        ${recentHtml}
        <p class="sb-section-label">${strings.featured}</p>
        <div class="sb-products-grid">${products.map((p, i) => productCard(p, i)).join('')}</div>
      `;
      body.querySelectorAll('.sb-recent-q').forEach(el => {
        el.addEventListener('click', () => { input.value = el.dataset.q; stopPlaceholderCycle(); const cb = document.getElementById('sb-search-clear'); if(cb) cb.style.display='flex'; doSearch(el.dataset.q); });
      });
      body.querySelectorAll('.sb-recent-x').forEach(el => {
        el.addEventListener('click', e => { e.stopPropagation(); clearRecentSearch(el.dataset.clear); renderPromoted(); });
      });
      body.querySelectorAll('.sb-product-card').forEach(wireCard);
    } catch(_) {
      body.innerHTML = '';
    }
  }

  /* ── Results ─────────────────────────────────────────────────────────────── */
  async function renderResults(products, mode, q, isCurrent = () => true) {
    clearTimeout(_loadingTimer);
    products = await localizeProducts(products);
    if (!isCurrent()) return;
    if (!products.length) {
      hideSizeBar();
      body.innerHTML = `
        <div class="sb-empty">
          <div style="font-size:2rem;margin-bottom:8px">🔍</div>
          ${strings.noResults} "<strong>${escHtml(q)}</strong>"<br>
          <span style="font-size:0.85rem;opacity:0.6;margin-top:6px;display:block">${strings.tryAnother}</span>
        </div>
        <div class="sb-ai-fallback">
          <span class="sb-ai-fallback-text">${strings.aiHelp}</span>
          <button class="sb-ai-fallback-btn" id="sb-chat-fallback">${strings.askAi}</button>
        </div>
        <div class="sb-pills" style="margin-top:20px;justify-content:center">
          ${POPULAR.map((p, i) => `<button class="sb-pill" data-q="${POPULAR_SEARCH_TERMS[i] || p}">${p}</button>`).join('')}
        </div>`;
      body.querySelectorAll('.sb-pill').forEach(btn => {
        btn.addEventListener('click', () => {
          input.value = btn.dataset.q;
          const cb = document.getElementById('sb-search-clear');
          if (cb) cb.style.display = 'flex';
          doSearch(btn.dataset.q);
        });
      });
      const chatFallback = document.getElementById('sb-chat-fallback');
      if (chatFallback) {
        chatFallback.addEventListener('click', () => {
          close();
          setTimeout(() => {
            if (window.SilverbackChat && window.SilverbackChat.open) window.SilverbackChat.open(q);
            else if (window.SbChat && window.SbChat.open) window.SbChat.open(q);
          }, 150);
        });
      }
      return;
    }

    const aBadge = mode === 'ai'
      ? `<div class="sb-ai-badge"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> ${strings.aiSearch}</div>`
      : '';
    const count = `<div class="sb-results-count">${products.length} ${products.length === 1 ? strings.result : strings.results}</div>`;

    const related = getRelatedSearches(q);
    const relatedHtml = related.length
      ? `<div class="sb-related"><p class="sb-section-label">${strings.related}</p><div class="sb-related-chips">${related.map(r => `<button class="sb-related-chip" data-q="${escHtml(r)}">${escHtml(r)}</button>`).join('')}</div></div>`
      : '';
    body.innerHTML = `${aBadge}${count}<div class="sb-products-grid">${products.map((p, i) => productCard(p, i)).join('')}</div>${relatedHtml}`;
    body.querySelectorAll('.sb-product-card').forEach(wireCard);
    body.querySelectorAll('.sb-related-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        input.value = chip.dataset.q;
        const cb = document.getElementById('sb-search-clear');
        if (cb) cb.style.display = 'flex';
        stopPlaceholderCycle(); doSearch(chip.dataset.q);
      });
    });
  }

  /* ── Loading ─────────────────────────────────────────────────────────────── */
  function renderLoading() {
    clearTimeout(_loadingTimer);
    _loadingTimer = setTimeout(() => {
      body.innerHTML = `<div class="sb-loading"><div class="sb-loading-dots"><span></span><span></span><span></span></div> ${strings.searching}</div>`;
    }, 200);
  }

  function localizedProductUrl(productUrl) {
    if (!productUrl || LOCALE_ROOT_URL === '/') return productUrl || '#';
    const url = new URL(productUrl, window.location.origin);
    const prefix = LOCALE_ROOT_URL.replace(/\/$/, '');
    if (url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`)) {
      url.pathname = `${prefix}${url.pathname.startsWith('/') ? '' : '/'}${url.pathname}`;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function formatProductPrice(priceInCents, fallbackPrice = '') {
    const numericPrice = Number(priceInCents);
    if (!Number.isFinite(numericPrice)) return fallbackPrice;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || LANG, {
        style: 'currency',
        currency: ACTIVE_CURRENCY,
      }).format(numericPrice / 100);
    } catch (_) {
      return `${ACTIVE_CURRENCY} ${(numericPrice / 100).toFixed(2)}`;
    }
  }

  async function localizeProducts(products) {
    if (LANG === 'en' || !products.length) return products;
    return Promise.all(products.map(async product => {
      if (!product.handle) return product;
      const cacheKey = `${LOCALE_ROOT_URL}:${product.handle}`;
      try {
        if (!localizedProductCache.has(cacheKey)) {
          const root = LOCALE_ROOT_URL === '/' ? '' : LOCALE_ROOT_URL.replace(/\/$/, '');
          localizedProductCache.set(cacheKey, fetch(`${root}/products/${encodeURIComponent(product.handle)}.js`)
            .then(response => {
              if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
              return response.json();
            })
            .catch(error => {
              localizedProductCache.delete(cacheKey);
              throw error;
            }));
        }
        const localized = await localizedProductCache.get(cacheKey);
        return {
          ...product,
          title: localized.title || product.title,
          type: localized.type || product.type,
          price: formatProductPrice(localized.price, product.price),
        };
      } catch (_) {
        return product;
      }
    }));
  }

  /* ── Product card ────────────────────────────────────────────────────────── */
  function productCard(p, idx) {
    const colours = (p.colours || []).slice(0, 6);
    const colourDots = colours.map(c =>
      `<span class="sb-colour-dot" style="background:${colourHex(c)}" title="${escHtml(c)}"></span>`
    ).join('');
    const img = p.image
      ? `<img src="${escHtml(p.image)}" alt="${escHtml(p.title)}" loading="lazy" />`
      : `<div style="width:100%;height:100%;background:#1c1c26;display:flex;align-items:center;justify-content:center;opacity:0.2"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>`;
    const inStock = (p.inStock || []).length > 0;
    const urgency = getUrgencyText(p);
    return `
      <a class="sb-product-card" href="${escHtml(localizedProductUrl(p.url))}"
         data-url="${escHtml(localizedProductUrl(p.url))}"
         data-handle="${escHtml(p.handle || '')}"
         data-title="${escHtml(p.title)}"
         data-price="${escHtml(p.price || '')}"
         data-pos="${idx || 0}">
        <div class="sb-product-img-wrap">
          ${img}
          <div class="sb-product-hover-cta"><span>VIEW PRODUCT</span></div>
        </div>
        <div class="sb-product-info">
          <div class="sb-product-type">${escHtml(p.type || '')}</div>
          <div class="sb-product-name">${escHtml(p.title)}</div>
          ${urgency ? `<div class="sb-urgency">⚡ ${urgency}</div>` : ''}
          <div class="sb-product-footer">
            <div class="sb-product-price">${escHtml(p.price || '')}</div>
            ${colourDots ? `<div class="sb-product-colours">${colourDots}</div>` : ''}
          </div>
        </div>
      </a>`;
  }

  function wireCard(card) {
    // Don't prevent default - let the anchor tag work naturally
    // Just update the href with UTM params before navigation
    card.addEventListener('click', e => {
      const rawUrl = card.dataset.url || card.getAttribute('href');
      const handle  = card.dataset.handle || '';
      const title   = card.dataset.title  || '';
      const price   = card.dataset.price  || '';
      const pos     = parseInt(card.dataset.pos || '0');
      const q       = lastQuery || '';
      const meta    = extractMetadata(q);
      
      // Track the click
      trackEvent({ type: 'click', query: q, product_handle: handle, product_title: title, product_price: price, position: pos, ...meta });
      
      // Update href with UTM params before navigation
      if (rawUrl && rawUrl !== '#') {
        try {
          const u = new URL(rawUrl);
          u.searchParams.set('utm_source',  'sb_search');
          u.searchParams.set('utm_medium',  'search_widget');
          if (q) u.searchParams.set('utm_term', q);
          if (handle) u.searchParams.set('utm_content', handle);
          if (meta.category) u.searchParams.set('utm_campaign', meta.category);
          if (meta.color) u.searchParams.set('utm_id', meta.color);
          card.setAttribute('href', u.toString());
        } catch(_) {}
      }
      
      // Close widget and let default anchor navigation happen
      close();
    });
  }

  /* ── Intercept Shopify search ────────────────────────────────────────────── */
  function interceptShopify() {
    // Selectors for silverbackgymwear.com theme + broad fallbacks
    const TRIGGER_SELECTORS = [
      // Silverback theme specific
      '.js-search-draw-icon',
      '.search-draw-trigger-icon',
      '.header-trigger__link--search',
      '.secondary-nav__link--search',
      // Common Shopify theme selectors
      '.header__search',
      '.search-modal__open-button',
      '[data-action="open-search"]',
      '[data-search-toggle]',
      '[aria-controls*="search" i]',
      '.predictive-search-toggle',
      '#search-modal-toggle',
      '.header-search-toggle',
    ];

    // Capture phase — fires before Shopify's own handlers
    document.addEventListener('click', function (e) {
      for (const sel of TRIGGER_SELECTORS) {
        try {
          if (e.target.closest(sel)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            open();
            return;
          }
        } catch (_) {}
      }
    }, true);

    // Intercept native search form submit
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.getAttribute && e.target.getAttribute('action') === '/search') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const q = (e.target.querySelector('input[type="search"], input[name="q"]') || {}).value || '';
        open(q.trim() || undefined);
      }
    }, true);

    // Suppress the <search-modal> web component and native modal entirely
    function suppressNativeSearch() {
      // Hide the native search modal element
      const nativeModal = document.getElementById('modal-search') ||
                          document.getElementById('SearchModal') ||
                          document.querySelector('search-modal');
      if (nativeModal) {
        nativeModal.style.cssText = 'display:none!important;visibility:hidden!important';
        // Stub out its open method if it's a web component
        try { nativeModal.open = function() {}; } catch(_) {}
        try { nativeModal.show = function() {}; } catch(_) {}
      }
      // Also hide any open search drawer (NOT the trigger button — only the drawer/modal content)
      const drawer = document.querySelector('.search-drawer, .js-search-drawer, .search__inner, .search__content-wrapper');
      if (drawer) drawer.style.cssText = 'display:none!important';
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', suppressNativeSearch);
    } else {
      suppressNativeSearch();
    }
    // Re-suppress if the theme opens it anyway (belt-and-braces)
    setTimeout(suppressNativeSearch, 800);
  }

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  function escHtml(s) {
    return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /* ── Expose API ──────────────────────────────────────────────────────────── */
  window.SilverbackSearch = { open, close };

  /* ── Boot ────────────────────────────────────────────────────────────────── */
  function boot() {
    inject();
    initPriceSlider();
    initSizeFilter();
    interceptShopify();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
